import type { Recipe } from './types'

type UseByDateOptions = {
  includeWeekday?: boolean
}

const parseDeliveryDate = (deliveryDate: string) => {
  const [, year, month, day] = /^(\d{4})-(\d{2})-(\d{2})/.exec(deliveryDate) ?? []

  if (!year || !month || !day) {
    return null
  }

  return new Date(Number(year), Number(month) - 1, Number(day))
}

const addDays = (date: Date, days: number) => {
  const nextDate = new Date(date)
  nextDate.setDate(nextDate.getDate() + days)

  return nextDate
}

const formatDate = (date: Date, includeWeekday: boolean) =>
  new Intl.DateTimeFormat('en-GB', {
    ...(includeWeekday ? { weekday: 'short' as const } : {}),
    day: 'numeric',
    month: 'short',
  })
    .format(date)
    .replace(',', '')

const formatDateRange = (startDate: Date, endDate: Date, includeWeekday: boolean) => {
  if (startDate.getTime() === endDate.getTime()) {
    return formatDate(startDate, includeWeekday)
  }

  if (includeWeekday || startDate.getMonth() !== endDate.getMonth()) {
    return `${formatDate(startDate, includeWeekday)} - ${formatDate(endDate, includeWeekday)}`
  }

  return `${startDate.getDate()} - ${formatDate(endDate, false)}`
}

export const formatUseByDateRange = (
  deliveryDate: string,
  shelfLife: Recipe['shelfLife'] | null | undefined,
  { includeWeekday = false }: UseByDateOptions = {},
) => {
  if (!deliveryDate || !shelfLife) {
    return null
  }

  const { minDays, maxDays } = shelfLife
  const parsedDeliveryDate = parseDeliveryDate(deliveryDate)

  if (!parsedDeliveryDate || minDays === undefined || maxDays === undefined) {
    return null
  }

  const startDate = addDays(parsedDeliveryDate, minDays)
  const endDate = addDays(parsedDeliveryDate, maxDays)

  return `Use by ${formatDateRange(startDate, endDate, includeWeekday)}`
}
